import * as React from "react";

import { UIFieldMap, useUiFieldMap } from "client/hooks";
import { FieldSets, MerchantFieldMapper } from "client/lib/apt-merchant";
import { dateInputMask } from "client/utils/input-masks";

import { FormBuilder } from "../FormBuilder";

import { useSubmitBusinessOwner } from "./useSubmitBusinessOwner";

export type BusinessOwnerFormUiFieldMap = MerchantFieldMapper<UIFieldMap>;

const singleOwnerFieldMap: Partial<
  FieldSets.BusinessOwnerValueMapper<UIFieldMap>
> = {
  firstName: {},
  lastName: {},
  dob: {
    inputMask: dateInputMask,
  },
  ssn: { width: 12 },
  street: { width: 12 },
  street2: { width: 12 },
  city: {},
  state: {},
  postalCode: {},
  govId: { width: 12 },
  govIdBackside: { width: 12 },
  verificationMethod: { width: 12 },
  certifiedGovIdPrimary: { width: 12 },
  certifiedGovIdSecondary: { width: 12 },
};

// field order reflects input order
export const businessOwnerUiFieldMap: BusinessOwnerFormUiFieldMap = {
  owners: [
    singleOwnerFieldMap,
    singleOwnerFieldMap,
    singleOwnerFieldMap,
    singleOwnerFieldMap,
    singleOwnerFieldMap,
  ],
};

export const FormWrapper: React.FC<{
  /**
   * Flag from parent to see if user accepted Merchant Agreement
   * and Privacy Policy
   */
  agreementAccepted: boolean;
  /**
   * Our core business owner form needs to inject dom between the
   * last field and the submit button
   */
  formFooter?: React.ReactNode;
  /**
   * Callback to handle any additional tasks onSubmit
   * e.g. for adding additional owners we close modal
   */
  handleModalClose?: (args?: any) => void;
  /**
   * The BusinessOwner to render
   */
  owner: FieldSets.BusinessOwner;
  ownerIndex: number;
  /**
   * We don't save form when adding additional business owners
   * If false, we delegate back to the consumer to handle
   */
  shouldSaveOnSubmit?: boolean;
  /**
   * What label to display on submit button.
   */
  submitLabel?: string;
}> = ({
  agreementAccepted,
  formFooter,
  handleModalClose,
  owner,
  ownerIndex,
  shouldSaveOnSubmit,
  submitLabel,
}) => {
  const { orderedUiFieldMap } = useUiFieldMap(businessOwnerUiFieldMap);
  const { completeBusinessOwners } = useSubmitBusinessOwner(agreementAccepted);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (shouldSaveOnSubmit) {
      completeBusinessOwners();
    }
    if (handleModalClose) {
      handleModalClose();
    }
  };

  return (
    <FormBuilder
      fieldSet={owner}
      footer={formFooter}
      onSubmit={handleSubmit}
      submitLabel={submitLabel}
      uiFieldMap={orderedUiFieldMap?.owners?.[ownerIndex]}
    />
  );
};
